import type { FC } from "react";
import { useState } from "react";

import clsx from "clsx";

import type { IUserCommunication } from "@/lib/common";
import type { IWhatIsCashback } from "@/src/cashback/LandingPage/WhatIsCashback";
import WhatIsCashback from "@/src/cashback/LandingPage/WhatIsCashback";
import UserCommunicationModal from "@/src/common/UserCommunicationModal";
import { languageSelector } from "@/src/contexts";
import MgmPhoneVerificationVerifyCode from "@/src/member-get-member/PhoneVerification/MgmPhoneVerificationVerifyCode";
import { MgmUserCommunicationAction } from "@/src/member-get-member/shared/MgmUserCommunicationAction";
import WebViewNavigation from "@/src/member-get-member/shared/WebViewNavigation";
import { browserStatics } from "@/src/shared/BrowserStatic";
import PageLayout from "@/src/shared/page-layout";
import PanelSection from "@/src/shared/panel-section";
import ImageLoader from "@Components/ImageLoader";
import MainLoading from "@Components/MainLoading";
import { TelemetryEntity, getCdnImage, scrollToY } from "@Components/Modules/Helpers";

import MgmPhoneVerificationResources from "./MgmPhoneVerification.Resources";
import MgmPhoneVerificationResourcesEn from "./MgmPhoneVerification.Resources.En";
import MgmPhoneVerificationSendCode from "./MgmPhoneVerificationSendCode";
import MgmPhoneVerificationSuccess from "./MgmPhoneVerificationSuccess";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmPhoneVerification.module.css";

export type IMgmPhoneVerification = {
    receiverBonusAmount: number;
    whatIsCashback: IWhatIsCashback;
    userCommunication?: IUserCommunication;
    isMobile: boolean;
};

export const MgmPhoneVerification: FC<IMgmPhoneVerification> = ({
    receiverBonusAmount,
    whatIsCashback,
    userCommunication,
    isMobile,
}) => {
    const resources = languageSelector(MgmPhoneVerificationResources, MgmPhoneVerificationResourcesEn);
    const [smsVerificationSuccess, setSmsVerificationSuccess] = useState(false);
    const [initialLoading, setShowInitialLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
    const [userCommunicationModal, setUserCommunicationModal] = useState(userCommunication);

    const isPhoneVerificationSuccess = phoneNumber && smsVerificationSuccess;

    return (
        <PageLayout isNewGrid breadcrumbs={isMobile && [{ labelKey: "mgm" }]}>
            <div className={portalCss.standardContainerV2}>
                <div
                    className={clsx(
                        css.mgmPhoneVerificationComponent,
                        portalCss.setSectionColOne,
                        initialLoading && css.initialLoading,
                        !phoneNumber && css.sendCode,
                    )}
                >
                    {browserStatics.isMobileApp && !smsVerificationSuccess && (
                        <WebViewNavigation
                            shouldCloseOnBackButtonClick={() => {
                                if (phoneNumber && !userCommunicationModal) {
                                    setPhoneNumber(null);
                                    return false;
                                }

                                return true;
                            }}
                        />
                    )}
                    <h1
                        className={css.title}
                        dangerouslySetInnerHTML={{
                            __html: isPhoneVerificationSuccess
                                ? resources.phoneVerificationSuccess.title
                                : resources.title,
                        }}
                    />
                    {initialLoading && <MainLoading />}
                    <ImageLoader
                        src={getCdnImage(
                            isPhoneVerificationSuccess
                                ? process.env.MGM_PhoneVerification_Success
                                : process.env.MGM_PhoneVerification,
                        )}
                    />
                    {!initialLoading && !phoneNumber && (
                        <MgmPhoneVerificationSendCode
                            receiverBonusAmount={receiverBonusAmount}
                            description={resources.phoneSendCode.description}
                            nextButtonText={resources.phoneSendCode.nextButtonText}
                            placeholder={resources.phoneSendCode.placeholder}
                            customErrorMessage={resources.phoneSendCode.genericError}
                            onPhoneSubmissionResponse={setPhoneNumber}
                        />
                    )}
                    {!initialLoading && phoneNumber && !smsVerificationSuccess && (
                        <MgmPhoneVerificationVerifyCode
                            description={resources.phoneSmsConfirmation.description}
                            details={resources.phoneSmsConfirmation.details}
                            nextButtonText={resources.phoneSmsConfirmation.nextButtonText}
                            prevButtonText={resources.phoneSmsConfirmation.prevButtonText}
                            placeholder={resources.phoneSmsConfirmation.placeholder}
                            resendSmsButtonText={resources.phoneSmsConfirmation.resendSmsButtonText}
                            resendSmsText={resources.phoneSmsConfirmation.resendSmsText}
                            customErrorMessage={resources.phoneSmsConfirmation.genericError}
                            phoneNumber={phoneNumber}
                            onClickBack={() => setPhoneNumber(null)}
                            onSubmissionResponse={(status: boolean) => {
                                setSmsVerificationSuccess(status);
                                setShowInitialLoading(false);
                                scrollToY(0);
                            }}
                            setCommunicationModal={setUserCommunicationModal}
                        />
                    )}
                    {!initialLoading && phoneNumber && smsVerificationSuccess && (
                        <MgmPhoneVerificationSuccess
                            description={resources.phoneVerificationSuccess.description}
                            details={resources.phoneVerificationSuccess.details}
                            nextButtonText={resources.phoneVerificationSuccess.nextButtonText}
                        />
                    )}
                </div>
                {!initialLoading && phoneNumber && smsVerificationSuccess && (
                    <PanelSection
                        {...resources.whatIsCashback}
                        className={css.whatIsCashbackPanel}
                        telemetryCustom={{
                            eventName: `${TelemetryEntity.Cashback}-WhatIsCashback`,
                            actionType: "View",
                        }}
                    >
                        <WhatIsCashback
                            {...whatIsCashback}
                            videoButtonText={resources.whatIsCashback.videoTitle}
                            showVideoButton={browserStatics.isMobileApp === false}
                        />
                    </PanelSection>
                )}
                {userCommunicationModal && (
                    <UserCommunicationModal
                        {...userCommunicationModal}
                        customActionClick={MgmUserCommunicationAction}
                    />
                )}
            </div>
        </PageLayout>
    );
};
